import { h } from '@stencil/core'
import { ObservableMap } from '@stencil/store'
import Axios from 'axios'
import { addToast, flashToast } from 'src/app/toast'
import { nameForCrm } from 'src/utils/crm'

import { ISalesforceField } from '../bookmarks/types'
import { ICrmContact, ICrmOpportunity } from '../call/types'
import { CloseioIntegrationStore, syncToCloseio } from './closeio/state'
import { HubspotIntegrationStore, syncToHubspot } from './hubspot/state'
import { SalesforceIntegrationStore, syncToSalesforce } from './salesforce/state'
import { CrmProvider } from './types'

export const CrmTimeoutMS = 30000

export interface ICrmState {
  showIntegration: boolean
  autoSyncEnabled: boolean
  connectedTo: string
  connectUrl: string
  disconnectUrl: string
  mappingUrl: string
  managedByEmail?: string
  refreshUrl: string
  syncUrl: string
  preferencesUrl: string
  taskSyncEnabled: boolean
  fields?: ISalesforceField[]
  contacts: ICrmContact[]
  opportunities: ICrmOpportunity[]
  isConnectDismissed: boolean
  isConnecting: boolean
  contactsCurrentlySyncing: boolean
  callCurrentlySyncing: boolean
  callLastSyncAt: string
  syncErrors: boolean
  syncTimedOut: boolean
  timeout?: number
  showingNoContactsToast: boolean
  manualContactSyncRequested: boolean
  refreshingContacts: boolean
  isTeamConnectionActive: boolean
}

export function getICrmStateDefaults(): ICrmState {
  return {
    showIntegration: false,
    autoSyncEnabled: false,
    connectedTo: '',
    connectUrl: null as string,
    disconnectUrl: null as string,
    mappingUrl: null as string,
    refreshUrl: null as string,
    syncUrl: null as string,
    preferencesUrl: null as string,
    taskSyncEnabled: true,
    contacts: [] as ICrmContact[],
    opportunities: [] as ICrmOpportunity[],
    isConnectDismissed: false,
    isConnecting: false,
    contactsCurrentlySyncing: false,
    callCurrentlySyncing: false,
    callLastSyncAt: '',
    syncErrors: false,
    syncTimedOut: false,
    timeout: null as number,
    showingNoContactsToast: false,
    manualContactSyncRequested: false,
    refreshingContacts: false,
    isTeamConnectionActive: false,
  }
}

export async function enrichContacts(crmState: ICrmState) {
  crmState.contactsCurrentlySyncing = true
  try {
    await Axios.post<string[]>(crmState.refreshUrl)
  } catch (ex) {
    crmState.contactsCurrentlySyncing = false
  }
}

export function currentCrm(): CrmProvider {
  if (SalesforceIntegrationStore.state.showIntegration) {
    return 'salesforce'
  } else if (HubspotIntegrationStore.state.showIntegration) {
    return 'hubspot'
  } else if (CloseioIntegrationStore.state.showIntegration) {
    return 'closeio'
  } else {
    return null
  }
}

export function crmSyncInProgress(): boolean {
  let value = false
  value ||= SalesforceIntegrationStore.state.showIntegration && SalesforceIntegrationStore.state.callCurrentlySyncing
  value ||= HubspotIntegrationStore.state.showIntegration && HubspotIntegrationStore.state.callCurrentlySyncing
  value ||= CloseioIntegrationStore.state.showIntegration && CloseioIntegrationStore.state.callCurrentlySyncing
  return value
}

export async function syncToCrm(isManualSync = false) {
  try {
    if (SalesforceIntegrationStore.state.showIntegration) {
      SalesforceIntegrationStore.state.syncErrors = false
      await syncToSalesforce(isManualSync)
    }
    if (HubspotIntegrationStore.state.showIntegration) {
      HubspotIntegrationStore.state.syncErrors = false
      await syncToHubspot(isManualSync)
    }
    if (CloseioIntegrationStore.state.showIntegration) {
      CloseioIntegrationStore.state.syncErrors = false
      await syncToCloseio(isManualSync)
    }
  } catch {
    if (SalesforceIntegrationStore.state.showIntegration) {
      flashToast({ type: 'alert', text: 'Error syncing to Salesforce!' })
    } else if (HubspotIntegrationStore.state.showIntegration) {
      flashToast({ type: 'alert', text: 'Error syncing to Hubspot!' })
    } else if (CloseioIntegrationStore.state.showIntegration) {
      flashToast({ type: 'alert', text: 'Error syncing to Close.io!' })
    }
  }
}

export function isCrmConnected(crmState: ICrmState = null) {
  if (crmState) {
    return !!crmState.connectedTo
  }
  return !!(HubspotIntegrationStore.state.connectedTo || SalesforceIntegrationStore.state.connectedTo)
}

export function showNoContactsToast(store: ObservableMap<ICrmState>, crm: CrmProvider, currentlySyncing: boolean) {
  const wasManualContactSyncRequested = store.state.manualContactSyncRequested
  if (!currentlySyncing) {
    store.state.manualContactSyncRequested = false
  }
  const shouldShowToast =
    !currentlySyncing &&
    !store.state.showingNoContactsToast &&
    wasManualContactSyncRequested &&
    store.state.contacts.length === 0

  if (shouldShowToast) {
    store.state.showingNoContactsToast = true
    addToast({
      delay: 1500,
      autoClose: false,
      color: 'info-warn',
      content: ({ dismiss }) => (
        <span class='flex items-center spacing-x-2'>
          <span>Add one of the attendees in {nameForCrm(crm)} in order to sync this call summary.</span>
          <ui-icon
            class='text-xl cursor-pointer'
            icon='x'
            onClick={() => {
              store.state.showingNoContactsToast = false
              dismiss()
            }}
          />
        </span>
      ),
    })
  }
}
